import type { FieldKind, NormRect } from './api'

/**
 * What each kind of box is called and how big it starts.
 *
 * Sizes are fractions of the page. They are the shapes these things actually
 * are on paper — a signature is a wide stroke, initials are nearly square, a
 * checkbox is small — so a freshly placed box usually needs no resizing.
 */
export interface KindSpec {
  kind: FieldKind
  label: string
  hint: string
  /** Whether the sender can attach their own wording, shown to the signer. */
  labelled: boolean
  size: { w: number; h: number }
}

export const KINDS: KindSpec[] = [
  {
    kind: 'signature',
    label: 'Signature',
    hint: 'Drawn by the signer. Exactly one per person: it is what the seal is anchored to.',
    labelled: false,
    size: { w: 0.3, h: 0.08 },
  },
  {
    kind: 'initials',
    label: 'Initials',
    hint: 'Defaulted from their name, and they can correct it.',
    labelled: false,
    size: { w: 0.08, h: 0.05 },
  },
  {
    kind: 'date_signed',
    label: 'Date signed',
    hint: 'Filled from the server clock when they sign. The signer cannot change it.',
    labelled: false,
    size: { w: 0.2, h: 0.04 },
  },
  {
    kind: 'text',
    label: 'Text',
    hint: 'Anything you need them to type, such as a job number, an address or a licence.',
    labelled: true,
    size: { w: 0.26, h: 0.04 },
  },
  {
    kind: 'checkbox',
    label: 'Checkbox',
    hint: 'A tick. Make it required for an "I agree" that has to be answered.',
    labelled: true,
    size: { w: 0.04, h: 0.03 },
  },
  {
    kind: 'radio',
    label: 'Radio',
    hint: 'One choice from a fixed set. The signer picks exactly one option.',
    labelled: true,
    size: { w: 0.28, h: 0.04 },
  },
  {
    kind: 'dropdown',
    label: 'Dropdown',
    hint: 'One choice from a list. Works well when there are several options.',
    labelled: true,
    size: { w: 0.28, h: 0.04 },
  },
]

/** Default choices when a sender first places a radio or dropdown field. */
export const DEFAULT_OPTIONS = ['Option 1', 'Option 2', 'Option 3']

export const kindNeedsOptions = (kind: FieldKind): boolean =>
  kind === 'radio' || kind === 'dropdown'

export const specFor = (kind: FieldKind): KindSpec =>
  KINDS.find((k) => k.kind === kind) ?? KINDS[0]

export const kindLabel = (kind: FieldKind): string => specFor(kind).label

/** True when a string looks like a value someone typed, not a field name. */
export function looksLikeFieldValue(s: string): boolean {
  const t = s.trim().replace(/[$,\s]/g, '')
  return t !== '' && !Number.isNaN(Number(t))
}

/** What the signer sees above the box — never a bare number mistaken for a name. */
export function signerFieldPrompt(label: string, kind: FieldKind): string {
  const trimmed = label.trim()
  if (kind === 'checkbox') return trimmed || 'I agree'
  if (kind === 'text') {
    if (trimmed && !looksLikeFieldValue(trimmed)) return trimmed
    return 'Amount'
  }
  if (trimmed) return trimmed
  return kindLabel(kind)
}

/** Default prompt when the sender adds a labelled box. */
export function defaultFieldLabel(kind: FieldKind, existing: readonly { kind: FieldKind }[]): string {
  if (!specFor(kind).labelled) return ''
  const n = existing.filter((f) => f.kind === kind).length + 1
  switch (kind) {
    case 'text':
      return 'Amount'
    case 'checkbox':
      return n === 1 ? 'I agree' : `Checkbox ${n}`
    default:
      return ''
  }
}

/** Sender-side name in lists and routing pickers — numbered, not the signer's answer. */
export function fieldDisplayName(
  fields: readonly { kind: FieldKind; label: string; documentIndex?: number; page: number }[],
  index: number,
  /**
   * What each document is called, when the envelope holds more than one. A box
   * listed as "page 1" means a different place on each of three documents, so
   * where there are several the name has to come first.
   */
  documentNames?: readonly string[],
): string {
  const f = fields[index]
  if (!f) return 'Field'
  const kind = kindLabel(f.kind)
  const where = documentNames && documentNames.length > 1
    ? `${documentNames[f.documentIndex ?? 0] ?? 'document'}, page ${f.page}`
    : `page ${f.page}`
  const sameKind = fields.filter((x) => x.kind === f.kind).length
  if (sameKind <= 1) return `${kind} · ${where}`
  const n = fields.slice(0, index + 1).filter((x) => x.kind === f.kind).length
  return `${kind} ${n} · ${where}`
}

/** A new box of this kind, nudged so successive ones do not land on each other. */
export function newRect(kind: FieldKind, index: number): NormRect {
  const { w, h } = specFor(kind).size
  const step = 0.07
  return {
    x: Math.min(0.08 + (index % 3) * 0.02, 1 - w),
    y: Math.min(0.2 + index * step, 0.95 - h),
    w,
    h,
  }
}
